import { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    DialogBackdrop,
} from '@headlessui/react';
import {
    LayoutTemplate,
    Import,
    FilePlus2,
    Search,
    X,
    ArrowLeft,
} from 'lucide-react';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
import useExperienceVersions from './hooks/useExperienceVersions';
import { Template, TemplateType } from '../Templates/hooks/useTemplates';

type SourceTab = 'template' | 'activation' | 'blank';

interface TemplateValueSpec {
    name?: string;
    description?: string;
    type?: string;
    defaultValue?: string;
    value?: string;
}

interface ActivationSummary {
    id: number;
    name: string;
    date?: string;
}

interface ExperienceSourceModalProps {
    activationId: number;
    isOpen: boolean;
    /** True when replacing an existing experience (warns; a version snapshot keeps it restorable). */
    isReplacing: boolean;
    onClose: () => void;
    /** Called after a template/import applied server-side — builder should reload. */
    onApplied: () => void;
    /** "Blank" tab: start from scratch (client-side scaffold). */
    onStartBlank: () => void;
}

/**
 * Common "experience source" UX: apply an org template, import another
 * activation's experience, or start blank. Used both for creating a new
 * experience and for replacing an existing one.
 */
export default function ExperienceSourceModal({
    activationId,
    isOpen,
    isReplacing,
    onClose,
    onApplied,
    onStartBlank,
}: ExperienceSourceModalProps) {
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();
    const { applyTemplate, importFromActivation } =
        useExperienceVersions(activationId);

    const [tab, setTab] = useState<SourceTab>('template');
    const [busy, setBusy] = useState(false);

    // Template tab state
    const [templates, setTemplates] = useState<Template[]>([]);
    const [templatesLoaded, setTemplatesLoaded] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
        null,
    );
    const [templateValues, setTemplateValues] = useState<{
        [key: string]: string;
    }>({});

    // Activation tab state
    const [activations, setActivations] = useState<ActivationSummary[]>([]);
    const [activationsLoaded, setActivationsLoaded] = useState(false);
    const [activationSearch, setActivationSearch] = useState('');

    useEffect(() => {
        if (!isOpen || !orgId) return;
        setSelectedTemplate(null);

        if (!templatesLoaded) {
            authenticatedApi
                .get(`/${orgId}/experience-templates`)
                .then((response) => {
                    const experienceTemplates = (
                        Array.isArray(response.data) ? response.data : []
                    ).filter(
                        (template: Template) =>
                            template.templateType === TemplateType.Experience,
                    );
                    setTemplates(experienceTemplates);
                })
                .catch(() => setTemplates([]))
                .finally(() => setTemplatesLoaded(true));
        }
        if (!activationsLoaded) {
            authenticatedApi
                .get(`/${orgId}/activations`)
                .then((response) => {
                    const rows = Array.isArray(response.data)
                        ? response.data
                        : [];
                    setActivations(
                        rows
                            .map((row: any) => ({
                                id: row.id,
                                name: row.name,
                                date: row.date,
                            }))
                            .filter(
                                (row: ActivationSummary) =>
                                    row.id !== activationId,
                            ),
                    );
                })
                .catch(() => setActivations([]))
                .finally(() => setActivationsLoaded(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, orgId]);

    // Parse the selected template's customizable values.
    const templateValueSpecs: { [key: string]: TemplateValueSpec } =
        useMemo(() => {
            if (!selectedTemplate) return {};
            try {
                const parsed = JSON.parse(
                    selectedTemplate.definitionJsonString,
                );
                return parsed.templateValues ?? {};
            } catch {
                return {};
            }
        }, [selectedTemplate]);

    useEffect(() => {
        const defaults: { [key: string]: string } = {};
        for (const key of Object.keys(templateValueSpecs)) {
            defaults[key] =
                templateValueSpecs[key].value ??
                templateValueSpecs[key].defaultValue ??
                '';
        }
        setTemplateValues(defaults);
    }, [templateValueSpecs]);

    const filteredActivations = useMemo(() => {
        if (!activationSearch) return activations;
        return activations.filter((activation) =>
            activation.name
                ?.toLowerCase()
                .includes(activationSearch.toLowerCase()),
        );
    }, [activations, activationSearch]);

    const handleApplyTemplate = async () => {
        if (!selectedTemplate) return;
        setBusy(true);
        const result = await applyTemplate(
            selectedTemplate.id,
            templateValues,
        );
        setBusy(false);
        if (result) {
            onApplied();
            onClose();
        }
    };

    const handleImport = async (sourceActivationId: number) => {
        setBusy(true);
        const result = await importFromActivation(sourceActivationId);
        setBusy(false);
        if (result) {
            onApplied();
            onClose();
        }
    };

    const tabs: { id: SourceTab; label: string; icon: any }[] = [
        { id: 'template', label: 'Template', icon: LayoutTemplate },
        { id: 'activation', label: 'From Activation', icon: Import },
        { id: 'blank', label: 'Blank', icon: FilePlus2 },
    ];

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-black/30" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
                <DialogPanel className="w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[85vh] flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <DialogTitle className="text-lg font-semibold text-gray-900">
                            {isReplacing
                                ? 'Replace Experience'
                                : 'Create Your Experience'}
                        </DialogTitle>
                        <button
                            onClick={onClose}
                            className="p-1 text-gray-400 hover:text-gray-600"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {isReplacing && (
                        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-800">
                            The current experience will be replaced. It stays
                            in version history and can be restored at any time.
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="flex border-b border-gray-200">
                        {tabs.map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                onClick={() => setTab(id)}
                                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                    tab === id
                                        ? 'border-blue-600 text-blue-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span>{label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        {tab === 'template' &&
                            (selectedTemplate ? (
                                <div>
                                    <button
                                        onClick={() =>
                                            setSelectedTemplate(null)
                                        }
                                        className="inline-flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        <span>All templates</span>
                                    </button>
                                    <h3 className="text-base font-semibold text-gray-900">
                                        {selectedTemplate.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 mb-4">
                                        {selectedTemplate.description}
                                    </p>

                                    {Object.keys(templateValueSpecs).length >
                                        0 && (
                                        <div className="space-y-4">
                                            {Object.entries(
                                                templateValueSpecs,
                                            ).map(([key, spec]) => (
                                                <div key={key}>
                                                    <label className="block text-sm font-medium text-gray-700">
                                                        {spec.name || key}
                                                    </label>
                                                    {spec.description && (
                                                        <p className="text-xs text-gray-500 mb-1">
                                                            {spec.description}
                                                        </p>
                                                    )}
                                                    {spec.type === 'color' ? (
                                                        <input
                                                            type="color"
                                                            value={
                                                                templateValues[
                                                                    key
                                                                ] ?? ''
                                                            }
                                                            onChange={(e) =>
                                                                setTemplateValues(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [key]: e
                                                                            .target
                                                                            .value,
                                                                    }),
                                                                )
                                                            }
                                                            className="h-9 w-16 border border-gray-300 rounded"
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            value={
                                                                templateValues[
                                                                    key
                                                                ] ?? ''
                                                            }
                                                            onChange={(e) =>
                                                                setTemplateValues(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [key]: e
                                                                            .target
                                                                            .value,
                                                                    }),
                                                                )
                                                            }
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <button
                                        onClick={handleApplyTemplate}
                                        disabled={busy}
                                        className="mt-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                                    >
                                        {busy
                                            ? 'Applying…'
                                            : 'Apply Template'}
                                    </button>
                                </div>
                            ) : !templatesLoaded ? (
                                <p className="text-sm text-gray-500 text-center py-8">
                                    Loading templates…
                                </p>
                            ) : templates.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-8">
                                    No experience templates are available for
                                    your organization.
                                </p>
                            ) : (
                                <div className="grid grid-cols-2 gap-3">
                                    {templates.map((template) => (
                                        <button
                                            key={template.id}
                                            onClick={() =>
                                                setSelectedTemplate(template)
                                            }
                                            className="text-left p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                                        >
                                            <h4 className="text-sm font-semibold text-gray-900">
                                                {template.name}
                                            </h4>
                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                                {template.description}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            ))}

                        {tab === 'activation' && (
                            <div>
                                <div className="relative mb-3">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search activations…"
                                        value={activationSearch}
                                        onChange={(e) =>
                                            setActivationSearch(e.target.value)
                                        }
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm"
                                    />
                                </div>
                                {!activationsLoaded ? (
                                    <p className="text-sm text-gray-500 text-center py-8">
                                        Loading activations…
                                    </p>
                                ) : (
                                    <ul className="divide-y divide-gray-200">
                                        {filteredActivations.map(
                                            (activation) => (
                                                <li
                                                    key={activation.id}
                                                    className="py-2 flex items-center justify-between"
                                                >
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {activation.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            #{activation.id}
                                                            {activation.date
                                                                ? ` · ${activation.date}`
                                                                : ''}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={() =>
                                                            handleImport(
                                                                activation.id,
                                                            )
                                                        }
                                                        disabled={busy}
                                                        className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md disabled:opacity-50"
                                                    >
                                                        Import
                                                    </button>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                                <p className="text-xs text-gray-400 mt-3">
                                    Importing copies the activation's current
                                    experience. Activations without an
                                    experience will report an error.
                                </p>
                            </div>
                        )}

                        {tab === 'blank' && (
                            <div className="text-center py-8">
                                <p className="text-sm text-gray-500 mb-4">
                                    Start with a single empty screen and build
                                    from scratch.
                                </p>
                                <button
                                    onClick={() => {
                                        onStartBlank();
                                        onClose();
                                    }}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
                                >
                                    Start from Scratch
                                </button>
                            </div>
                        )}
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}
